import { ArrowUpRightFromSquare, ChevronDown, InfoCircle } from 'flowbite-react-icons/outline'
import { useId, useState } from 'react'

/** Primary message shown in collapsed and expanded states (Figma R01/R02). */
export const CLINICAL_SUMMARY_INFO_BANNER_INTRO =
  'A detailed summary and thorough phenotype terms enhance diagnostic accuracy.'

/** Disclosure control label; chevron indicates expand/collapse. */
export const CLINICAL_SUMMARY_INFO_BANNER_TOGGLE_LABEL = 'What to include'

/** Bullets revealed when the disclosure is expanded (Figma R02). */
export const CLINICAL_SUMMARY_INFO_BANNER_INCLUDE_ITEMS = [
  'Presenting features and when they started',
  'Exam findings',
  'Labs, imaging, EEG',
  'Growth and feeding',
  'What has been ruled out or not observed',
] as const

export const CLINICAL_SUMMARY_INFO_BANNER_HPO_LINK_LABEL =
  'Learn about Human Phenotype Ontology terms'

export const CLINICAL_SUMMARY_INFO_BANNER_HPO_LINK_URL = 'https://hpo.jax.org/'

/**
 * Collapsible guidance banner for the Clinical Summary HPO editor empty state.
 * Visual layout mirrors `.info-banner` / {@link EditFlowStaleHpoBanner} but uses
 * a compact Figma padding and an inline “What to include” disclosure.
 */
export function ClinicalSummaryInfoBanner() {
  const [expanded, setExpanded] = useState(false)
  const panelId = useId()

  return (
    <div
      className="flex flex-col gap-0.5 rounded-base border border-border-light dark:border-gray-700 bg-background-brand-softer dark:bg-gray-800 px-3.5 py-2 sm:px-3.5"
      data-testid="clinical-summary-info-banner"
    >
      <div className="flex items-start gap-2.5">
        <InfoCircle className="mt-0.5 h-4 w-4 shrink-0 text-brand-700 dark:text-brand-400" aria-hidden />
        <p className="min-w-0 flex-1 text-sm leading-5 text-text-fg-info dark:text-brand-300">{CLINICAL_SUMMARY_INFO_BANNER_INTRO}</p>
      </div>

      <div className="pl-6">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 px-1 py-0.5 text-sm font-medium text-text-fg-brand dark:text-brand-400 hover:text-brand-600 dark:hover:text-brand-300"
          aria-expanded={expanded}
          aria-controls={panelId}
          data-testid="clinical-summary-info-banner-toggle"
        >
          {CLINICAL_SUMMARY_INFO_BANNER_TOGGLE_LABEL}
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-brand-700 dark:text-brand-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </div>

      {/* Stays mounted so `aria-controls` always resolves. */}
      <div
        id={panelId}
        hidden={!expanded}
        className="flex flex-col gap-0.5 pl-6 pt-1.5"
        data-testid="clinical-summary-info-banner-panel"
      >
        <ul className="flex flex-col gap-0.5 text-sm leading-5 text-text-heading dark:text-white">
          {CLINICAL_SUMMARY_INFO_BANNER_INCLUDE_ITEMS.map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-text-subtle" aria-hidden />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <a
          href={CLINICAL_SUMMARY_INFO_BANNER_HPO_LINK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-fit items-center gap-1.5 pb-1 pt-2 text-sm font-medium text-text-fg-brand dark:text-brand-400 hover:text-brand-600 dark:hover:text-brand-300"
          data-testid="clinical-summary-info-banner-hpo-link"
        >
          {CLINICAL_SUMMARY_INFO_BANNER_HPO_LINK_LABEL}
          <ArrowUpRightFromSquare className="h-[13px] w-[13px] shrink-0 text-brand-700 dark:text-brand-400" aria-hidden />
        </a>
      </div>
    </div>
  )
}
